import React, { FormEvent, useState } from 'react';
import axios from 'axios';
import Modal from './Modal';
import Button from './Button';
import ErrorBanner from './ErrorBanner';
import { buscarPacientes, PacienteResponse } from '../api/pacienteApi';
import { actualizarTurno, crearTurno, TurnoResponse } from '../api/turnoApi';
import { ApiError } from '../types/auth.types';
import nuevoAnalisisStyles from '../pages/NuevoAnalisisPage.module.css';
import adminStyles from '../pages/AdminPage.module.css';
import styles from './TurnoModal.module.css';

interface TurnoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (turno: TurnoResponse) => void;
  editingTurno?: TurnoResponse | null;
}

function today(): string {
  return new Date().toISOString().split('T')[0];
}

export default function TurnoModal({ isOpen, onClose, onSaved, editingTurno }: TurnoModalProps) {
  const isEditing = !!editingTurno;

  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState<PacienteResponse[]>([]);
  const [searchingPatient, setSearchingPatient] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PacienteResponse | null>(null);
  const [patientSearched, setPatientSearched] = useState(false);

  const [fecha, setFecha] = useState(today());
  const [hora, setHora] = useState('06:00');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    if (editingTurno) {
      setFecha(editingTurno.fecha);
      setHora(editingTurno.hora.slice(0, 5));
    } else {
      setFecha(today());
      setHora('06:00');
    }
  }, [isOpen, editingTurno]);

  function resetAndClose() {
    setPatientQuery('');
    setPatientResults([]);
    setSelectedPatient(null);
    setPatientSearched(false);
    setFecha(today());
    setHora('06:00');
    setError(null);
    onClose();
  }

  async function handlePatientSearch(e: FormEvent) {
    e.preventDefault();
    if (!patientQuery.trim()) return;
    setSearchingPatient(true);
    setPatientSearched(true);
    try {
      const results = await buscarPacientes(patientQuery.trim());
      setPatientResults(results);
    } finally {
      setSearchingPatient(false);
    }
  }

  function selectPatient(p: PacienteResponse) {
    setSelectedPatient(p);
    setPatientResults([]);
    setPatientQuery('');
    setPatientSearched(false);
  }

  function clearPatient() {
    setSelectedPatient(null);
    setPatientResults([]);
    setPatientQuery('');
    setPatientSearched(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!isEditing && !selectedPatient) { setError('Seleccioná un paciente.'); return; }
    if (!fecha || !hora) { setError('Completá la fecha y el horario.'); return; }

    setLoading(true);
    try {
      const turno = isEditing
        ? await actualizarTurno(editingTurno!.id, { fecha, hora: `${hora}:00` })
        : await crearTurno({ pacienteId: selectedPatient!.id, fecha, hora: `${hora}:00` });
      onSaved(turno);
      resetAndClose();
    } catch (err) {
      if (axios.isAxiosError<ApiError>(err)) {
        setError(err.response?.data?.mensaje ?? 'No se pudo guardar el turno.');
      } else {
        setError('No se pudo guardar el turno.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={isEditing ? 'Editar turno' : 'Registrar turno'}
      width={480}
      disableClose={loading}
    >
      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
      <form onSubmit={handleSubmit} noValidate>
        <div className={adminStyles.field}>
          <label className={adminStyles.label}>Paciente <span className={adminStyles.required}>*</span></label>
          {isEditing ? (
            <div className={nuevoAnalisisStyles.selectedPatient}>
              <div className={nuevoAnalisisStyles.selectedPatientInfo}>
                <div className={nuevoAnalisisStyles.selectedPatientName}>
                  {editingTurno!.pacienteApellido}, {editingTurno!.pacienteNombre}
                </div>
                <div className={nuevoAnalisisStyles.selectedPatientDni}>DNI {editingTurno!.pacienteDni}</div>
              </div>
            </div>
          ) : selectedPatient ? (
            <div className={nuevoAnalisisStyles.selectedPatient}>
              <div className={nuevoAnalisisStyles.selectedPatientInfo}>
                <div className={nuevoAnalisisStyles.selectedPatientName}>
                  {selectedPatient.apellido}, {selectedPatient.nombre}
                </div>
                <div className={nuevoAnalisisStyles.selectedPatientDni}>DNI {selectedPatient.dni}</div>
              </div>
              <button
                type="button"
                className={nuevoAnalisisStyles.clearBtn}
                onClick={clearPatient}
                title="Cambiar paciente"
              >
                ✕
              </button>
            </div>
          ) : (
            <>
              <div className={nuevoAnalisisStyles.searchRow}>
                <input
                  className={nuevoAnalisisStyles.searchInput}
                  type="text"
                  placeholder="Buscar por nombre, apellido o DNI..."
                  value={patientQuery}
                  onChange={(e) => setPatientQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handlePatientSearch(e as unknown as FormEvent); }
                  }}
                />
                <button
                  type="button"
                  className={nuevoAnalisisStyles.searchBtn}
                  onClick={handlePatientSearch}
                  disabled={searchingPatient || !patientQuery.trim()}
                >
                  {searchingPatient ? 'Buscando...' : 'Buscar'}
                </button>
              </div>

              {patientResults.length > 0 ? (
                <ul className={nuevoAnalisisStyles.patientList}>
                  {patientResults.map((p) => (
                    <li key={p.id} className={nuevoAnalisisStyles.patientItem} onClick={() => selectPatient(p)}>
                      <div className={nuevoAnalisisStyles.patientItemName}>{p.apellido}, {p.nombre}</div>
                      <div className={nuevoAnalisisStyles.patientItemDni}>DNI {p.dni}</div>
                    </li>
                  ))}
                </ul>
              ) : patientSearched && !searchingPatient ? (
                <p className={nuevoAnalisisStyles.noResults}>No se encontró ningún paciente con ese criterio.</p>
              ) : null}

              <div className={styles.createPatientRow}>
                <span className={nuevoAnalisisStyles.createPatientHint}>¿El paciente no está cargado?</span>
                <a
                  href="/pacientes?crear=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={nuevoAnalisisStyles.createPatientLink}
                >
                  Crear paciente →
                </a>
              </div>
            </>
          )}
        </div>

        <div className={adminStyles.formRow}>
          <div className={adminStyles.field}>
            <label className={adminStyles.label}>Fecha <span className={adminStyles.required}>*</span></label>
            <input
              className={adminStyles.input}
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>
          <div className={adminStyles.field}>
            <label className={adminStyles.label}>Hora <span className={adminStyles.required}>*</span></label>
            <input
              className={adminStyles.input}
              type="time"
              min="06:00"
              max="13:00"
              step={900}
              value={hora}
              onChange={(e) => setHora(e.target.value)}
            />
          </div>
        </div>
        <p className={styles.hint}>Los turnos se agendan de lunes a viernes, de 6:00 a 13:00 hs.</p>

        <div className={adminStyles.formActions}>
          <Button type="submit" loading={loading} loadingText="Guardando...">
            {isEditing ? 'Guardar cambios' : 'Registrar turno'}
          </Button>
          <button type="button" className={adminStyles.cancelBtn} onClick={resetAndClose} disabled={loading}>
            Cancelar
          </button>
        </div>
      </form>
    </Modal>
  );
}
